﻿import {Delegate} from "../Tools/Delegate";
import {IDisposableLike} from "../Tools/Disposable/IDisposableLike";
import type * as monacoType from "monaco-editor";


export interface ITemplateBodyHtmlEditorOption {
    jqMainContainer?: JQuery | undefined;
    model: string;
    titre?: string | undefined;
    language: "plaintext" | "html" | "json";
    onDispose?: (sender: MonacoEditorComponent) => void
    onChange: (value: string) => void
}

export class MonacoEditorComponent implements  IDisposableLike {
    public MainContainer: JQuery | undefined;
    public Titre: string | undefined;
    public OnDispose: Delegate | undefined;
    
    readonly #onChange: Delegate | undefined
    readonly #language: "plaintext" | "html" | "json"
    #model: string | undefined;
    #jqContainer: JQuery | undefined;
    #jqMonaco: JQuery | undefined;
    #monacoEditor: monacoType.editor.IStandaloneCodeEditor | undefined;
    #monacoModel: monacoType.editor.ITextModel | undefined;

    constructor(option: ITemplateBodyHtmlEditorOption) {
        this.MainContainer = option.jqMainContainer;
        this.#model = option.model;
        this.Titre = option.titre;
        
        this.#onChange = new Delegate();
        
        if (option.onChange != null)
            this.#onChange.Bind(option.onChange);
            
        
        this.#language = option.language;

        this.OnDispose = new Delegate();

        if (option.onDispose != null)
            this.OnDispose.Bind(option.onDispose);
    }

    public Init = async () => {
        await this.#buildHtml();
        await this.#buildMonaco();
    };

    #buildHtml = async () => {
        if (!this.MainContainer) throw new Error('MainContainer manquant');

        this.#jqContainer = $('<div></div>')
            .css({
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
            })
            .appendTo(this.MainContainer);
        

        this.#jqMonaco = $("<div></div>")
            .css({
               width: "100%",
                height: "100%",
            })
            .appendTo(this.#jqContainer);

    };

    #buildMonaco = async () => {

        const { monaco } = await import("../monaco/initMonaco.js");

        if (!monaco) {
            throw new Error("Monaco n'est pas initialisé");
        }

        const value = this.#model ?? "";

        console.log(this.#language)
        this.#monacoModel = monaco.editor.createModel(
            value,
            this.#language
        );
        
        // Validation initiale (html / json uniquement).
        if (this.#language === "html" || this.#language === "json")
            await this.#validate();

        // Écouter les changements de contenu : on émet TOUJOURS onChange (tous
        // langages, plaintext compris) pour que le modèle soit marqué dirty et
        // sauvegardé ; la re-validation ne s'applique qu'au html / json.
        this.#monacoModel.onDidChangeContent(async () => {
            if (this.#language === "html" || this.#language === "json")
                await this.#validate();
            this.#onChange.Fire(this.#monacoModel.getValue());
        });

        this.#monacoEditor = monaco.editor.create(this.#jqMonaco.get(0) as HTMLElement, {
            model: this.#monacoModel,
            automaticLayout: true,
            theme: "vs-light",
        });

        // Formater automatiquement le JSON à l'ouverture
        if (this.#language === "json") {
            await this.#monacoEditor.getAction('editor.action.formatDocument')?.run();
        }
    }

    #validate = async () => {
        const { monaco, validateHTMLWithCSS, validateJSON } = await import("../monaco/initMonaco.js");

        let markers: monacoType.editor.IMarkerData[] = [];

        if (this.#language === "html") {
            markers = validateHTMLWithCSS(this.#monacoModel);
        } else if (this.#language === "json") {
            markers = validateJSON(this.#monacoModel);
        }

        monaco.editor.setModelMarkers(this.#monacoModel, 'validation', markers);
    }

    public Dispose = () => {
        this.#monacoModel?.dispose();
        this.#monacoEditor?.dispose();
        this.#jqContainer?.remove();
        this.OnDispose?.Fire(this);
    };

}
